import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { CameraRef } from '@maplibre/maplibre-react-native';

import AffiliationBadge from './AffiliationBadge';
import CurrentLocationButton from './CurrentLocationButton';
import GuidesButton from './GuidesButton';
import NewsButton from './NewsButton';
import SearchBar from './SearchBar';
import SearchOverlay from './SearchOverlay';
import StationCard from './StationCard';
import StationListPanel from './StationListPanel';
import StationListToggleButton from './StationListToggleButton';
import StationMapView from './StationMapView';
import { useMapCenterCache } from '../hooks/useMapCenterCache';
import { useSelectedStation } from '../hooks/useSelectedStation';
import { useTileLayer } from '../hooks/useTileLayer';
import { useUserCoordinates } from '../hooks/useUserCoordinates';
import { useFocusOnCurrentLocation } from '../hooks/useFocusOnCurrentLocation';
import type { Affiliation } from '../types/affiliation';
import type { Station } from '../types/station';
import type { Coordinates } from '../utils/geolocation';
import { INITIAL_ZOOM } from '../utils/mapConfig';
import { COLORS } from '../config/colors';

type MapWrapperProps = {
  stations: Station[];
  affiliation: Affiliation | null;
};

const MapWrapper = ({ stations, affiliation }: MapWrapperProps) => {
  const tilePath = useTileLayer();
  const { coordinates } = useUserCoordinates();
  const {
    initialCenter,
    isLoading: isMapCenterLoading,
    onCenterChange,
  } = useMapCenterCache();
  const cameraRef = useRef<CameraRef>(null);

  // A map is only created at the center it is given, so the center it should
  // open at is held here and refreshed every time it is mounted again (the
  // station list closed, or a station picked from that list).
  const [mapStartCenter, setMapStartCenter] = useState<Coordinates | null>(
    null,
  );
  // Where the map is currently looking, reported by its movement events.
  const visibleCenterRef = useRef<Coordinates | null>(null);

  // Open the map where the user last left it; fall back to the user's location
  // (or the default center) when nothing has been cached yet.
  const mapCenter = mapStartCenter ?? initialCenter ?? coordinates;
  const focusOnCurrentLocation = useFocusOnCurrentLocation(cameraRef);
  const { selectedStation, selectStation, deselectStation } =
    useSelectedStation();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isListVisible, setIsListVisible] = useState(false);

  const handleCenterChange = useCallback(
    (center: Coordinates) => {
      visibleCenterRef.current = center;
      onCenterChange(center);
    },
    [onCenterChange],
  );

  const handleStationPress = useCallback(
    (station: Station) => {
      selectStation(station);
    },
    [selectStation],
  );

  const handleMapPress = useCallback(() => {
    deselectStation();
  }, [deselectStation]);

  const handleSelectStation = useCallback(
    (station: Station) => {
      cameraRef.current?.flyTo({
        center: [station.longitude, station.latitude],
        zoom: INITIAL_ZOOM,
        duration: 1500,
      });
      selectStation(station);
    },
    [selectStation],
  );

  // Selecting from the list also closes it so the map (and the station card)
  // become visible again. The map is not mounted while the list is open, so it
  // is opened on the station instead of flying to it.
  const handleSelectStationFromList = useCallback(
    (station: Station) => {
      setMapStartCenter({
        latitude: station.latitude,
        longitude: station.longitude,
      });
      selectStation(station);
      setIsListVisible(false);
    },
    [selectStation],
  );

  const handleToggleList = useCallback(() => {
    // Reopen the map where the user was looking when the list was opened.
    setMapStartCenter(visibleCenterRef.current);
    setIsListVisible(isVisible => !isVisible);
  }, []);

  // Wait for the cached map center to be read so the map is not created with
  // the default center and then re-centered.
  if (!tilePath || isMapCenterLoading) {
    return <View style={styles.container} />;
  }

  return (
    <View style={styles.container}>
      {/* Either the map or the station list is shown: the list is mounted only
          while it is toggled on (so it loads lazily) and the map, with its
          markers, is unmounted while the list takes its place. */}
      {isListVisible ? (
        <StationListPanel
          stations={stations}
          mapCenter={mapCenter}
          onSelectStation={handleSelectStationFromList}
        />
      ) : (
        <>
          <StationMapView
            cameraRef={cameraRef}
            tilePath={tilePath}
            stations={stations}
            longitude={mapCenter.longitude}
            latitude={mapCenter.latitude}
            onMapPress={handleMapPress}
            onStationPress={handleStationPress}
            onCenterChange={handleCenterChange}
          />

          {!isSearchExpanded ? (
            <CurrentLocationButton onPress={focusOnCurrentLocation} />
          ) : null}

          <AffiliationBadge affiliation={affiliation} />

          {isSearchExpanded ? (
            <SearchOverlay
              stations={stations}
              onClose={() => setIsSearchExpanded(false)}
              onSelectStation={handleSelectStation}
            />
          ) : (
            !selectedStation && (
              <SearchBar onPress={() => setIsSearchExpanded(true)} />
            )
          )}

          {selectedStation ? <StationCard station={selectedStation} /> : null}
        </>
      )}

      {/* Kept above the list so the list can be toggled back, plus the
          buttons that switch to the news and guides screens. Together they
          form the map's right-hand button column, with the station list
          toggle on top. */}
      {!isSearchExpanded ? (
        <StationListToggleButton
          isOpen={isListVisible}
          onPress={handleToggleList}
        />
      ) : null}

      {!isSearchExpanded ? <NewsButton /> : null}

      {!isSearchExpanded ? <GuidesButton /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.articleOddBackground
  },
});

export default MapWrapper;
